import { cn } from '@/lib/utils'

interface SearchResultCardProps {
  id: string
  name: string
  bestmenu: string
  bestmenu1?: string
  bestmenu2?: string
  bestmenu3?: string
  pickupInstructions: string
  isOpen: boolean
  isNew?: boolean
  createdAt: string
  storeType?: string
  imageUrl?: string
  products?: Array<{
    id: string
    name: string
    image_url?: string
  }>
  onClick: () => void
  className?: string
}

export function SearchResultCard({
  id: _,
  name,
  bestmenu,
  bestmenu1,
  bestmenu2,
  bestmenu3,
  pickupInstructions: __,
  isOpen,
  isNew,
  createdAt,
  storeType,
  imageUrl: ___,
  products,
  onClick,
  className
}: SearchResultCardProps) {
  // 실제 상품 이미지 URL 가져오기 또는 placeholder 사용
  const getImageUrl = () => {
    // 1. 첫 번째 상품의 이미지가 있으면 사용 (DB에서 직접 가져온 실제 URL)
    if (products && products.length > 0) {
      for (const product of products) {
        if (product.image_url) {
          // Supabase storage URL을 직접 사용하여 CORB 이슈 방지
          return product.image_url;
        }
      }
    }
    
    // 2. 매장 자체 이미지가 있으면 사용
    if (___) {
      return ___;
    }
    
    // 3. 모든 이미지가 없으면 placeholder.png 사용
    return '/images/placeholder.png';
  };
  
  const foodImageUrl = getImageUrl();

  // 대표메뉴 3개를 조합하는 함수 (StoreCard와 동일한 로직)
  const getBestMenuText = () => {
    const menus = [];
    
    // bestmenu1, bestmenu2, bestmenu3이 있으면 사용
    if (bestmenu1) menus.push(bestmenu1);
    if (bestmenu2) menus.push(bestmenu2);
    if (bestmenu3) menus.push(bestmenu3);
    
    // 3개 필드가 모두 없으면 레거시 bestmenu 사용
    if (menus.length === 0 && bestmenu) {
      return bestmenu;
    }
    
    // | 로 구분하여 조합
    return menus.join(' | ');
  };

  // 매장이 신규인지 확인 (isNew 플래그 또는 등록일 기준 일주일 이내)
  const isNewStore = () => {
    // isNew 플래그가 있으면 우선 사용
    if (isNew !== undefined) {
      return isNew;
    }
    
    // 없으면 등록일로 판단
    const createdDate = new Date(createdAt)
    const now = new Date()
    const weekInMs = 7 * 24 * 60 * 60 * 1000 // 일주일 = 7일 * 24시간 * 60분 * 60초 * 1000ms
    return (now.getTime() - createdDate.getTime()) < weekInMs
  }

  // 매장 타입을 한글로 변환
  const getStoreTypeLabel = (type?: string) => {
    const typeMap: { [key: string]: string } = {
      'korean': '한식',
      'chinese': '중식',
      'japanese': '일식', 
      'western': '양식',
      'cafe': '카페',
      'lunchbox': '도시락',
      'kimbap': '김밥',
      'salad': '샐러드',
      'bakery': '베이커리',
      'buffet': '뷔페',
      'etc': '기타'
    }
    return type ? typeMap[type] || type : ''
  }

  return (
    <button
      onClick={onClick}
      className={cn(
        "w-full flex items-center gap-3 p-4 bg-white border-b border-[#F5F5F5] cursor-pointer hover:bg-[#FAFAFA] transition-colors text-left",
        className
      )}
    >
      {/* 음식 이미지 */}
      <div className="relative flex-shrink-0 w-24 h-24 bg-[#F5F5F5] rounded-lg overflow-hidden">
        <img
          src={foodImageUrl}
          alt={getBestMenuText()}
          className="w-full h-full object-cover"
          loading="lazy"
          onError={(e) => {
            e.currentTarget.src = '/images/placeholder.png'
          }}
        />
      </div>

      {/* 매장 정보 */}
      <div className="flex-1 min-w-0">
        {/* 상단 배지 */}
        <div className="flex items-center gap-1 mb-2">
          {isOpen ? (
            <span className="inline-flex items-center px-2 py-0.5 bg-[#FFEFE0] text-[#FF6D00] text-[12px] font-medium rounded">
              주문가능
            </span>
          ) : (
            <span className="inline-flex items-center px-2 py-0.5 bg-[#999999] text-white text-[12px] font-medium rounded">
              영업종료
            </span>
          )}
          {isNewStore() && (
            <span className="inline-flex items-center px-2 py-0.5 bg-[#FF6D00] text-white text-[12px] font-medium rounded">
              New
            </span>
          )}
          <span className="inline-flex items-center px-2 py-0.5 bg-[#51c854] text-white text-[12px] font-medium rounded">
            마감할인
          </span>
        </div>
        
        {/* 매장명 */}
        <h3 className="text-[18px] font-bold text-[#222222] mb-2 truncate">
          {name}
        </h3>
        
        {/* 하단 정보 */}
        <div className="flex items-center gap-2">
          <p className="text-[14px] text-[#666666] truncate flex-1">
            {getBestMenuText()}
          </p>
          {storeType && (
            <span className="text-[12px] text-[#999999] bg-[#F5F5F5] px-2 py-0.5 rounded">
              {getStoreTypeLabel(storeType)}
            </span>
          )}
        </div>
      </div>
    </button>
  )
}